import React, { useState, useEffect, useCallback, useRef } from 'react';
import tradingHoursService from './tradingHoursService';
import './CompactTradingHours.css';

const CompactTradingHours = ({ onChange, initialHours }) => {
  const [hours, setHours] = useState({
    monday: { open: true, start: '09:00', end: '17:00' },
    tuesday: { open: true, start: '09:00', end: '17:00' },
    wednesday: { open: true, start: '09:00', end: '17:00' },
    thursday: { open: true, start: '09:00', end: '17:00' },
    friday: { open: true, start: '09:00', end: '17:00' },
    saturday: { open: false, start: '09:00', end: '17:00' },
    sunday: { open: false, start: '09:00', end: '17:00' }
  });

  const [totalDays, setTotalDays] = useState(0);
  const [totalHours, setTotalHours] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  
  const onChangeRef = useRef(onChange);
  const initialLoadRef = useRef(false);
  const saveTimeoutRef = useRef(null);
  
  // Keep onChange ref current
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Format time to ensure HH:MM format and remove extra colons
  const formatTime = useCallback((timeString) => {
    if (!timeString) return '09:00';
    
    // Remove extra colons and clean up the string
    const cleaned = timeString.replace(/::+/g, ':').replace(/:$/, '').trim();
    
    // Handle special case of 00:00 (treat as 09:00 for UI)
    if (cleaned === '00:00') return '09:00';
    
    // Validate HH:MM format
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (timeRegex.test(cleaned)) {
      // Ensure 2-digit hour format
      const [hour, minute] = cleaned.split(':');
      return `${hour.padStart(2, '0')}:${minute}`;
    }
    
    // Fallback for invalid times
    console.warn(`Invalid time format: ${timeString}, using fallback`);
    return '09:00';
  }, []);

  // Load trading hours from database on mount
  useEffect(() => {
    if (initialLoadRef.current) {
      console.log('Initial load already completed, skipping...');
      return;
    }
    
    console.log('Starting initial load of trading hours...');
    initialLoadRef.current = true;
    
    const loadTradingHours = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        console.log('Loading trading hours from API...');
        
        const data = await tradingHoursService.loadTradingHours();
        console.log('Trading hours API response:', data);
        
        if (data) {
          // Convert flat API response to component format
          const formattedHours = {
            monday: {
              open: data.monday_open !== undefined ? data.monday_open : true,
              start: formatTime(data.monday_start || '09:00'),
              end: formatTime(data.monday_end || '17:00')
            },
            tuesday: {
              open: data.tuesday_open !== undefined ? data.tuesday_open : true,
              start: formatTime(data.tuesday_start || '09:00'),
              end: formatTime(data.tuesday_end || '17:00')
            },
            wednesday: {
              open: data.wednesday_open !== undefined ? data.wednesday_open : true,
              start: formatTime(data.wednesday_start || '09:00'),
              end: formatTime(data.wednesday_end || '17:00')
            },
            thursday: {
              open: data.thursday_open !== undefined ? data.thursday_open : true,
              start: formatTime(data.thursday_start || '09:00'),
              end: formatTime(data.thursday_end || '17:00')
            },
            friday: {
              open: data.friday_open !== undefined ? data.friday_open : true,
              start: formatTime(data.friday_start || '09:00'),
              end: formatTime(data.friday_end || '17:00')
            },
            saturday: {
              open: data.saturday_open !== undefined ? data.saturday_open : false,
              start: formatTime(data.saturday_start || '09:00'),
              end: formatTime(data.saturday_end || '17:00')
            },
            sunday: {
              open: data.sunday_open !== undefined ? data.sunday_open : false,
              start: formatTime(data.sunday_start || '09:00'),
              end: formatTime(data.sunday_end || '17:00')
            }
          };
          
          setHours(formattedHours);
          setTotalDays(data.total_working_days || 0);
          setTotalHours(data.total_weekly_hours || 0);
          
          console.log('Trading hours loaded and formatted successfully');
        } else {
          console.warn('No trading hours data in response');
          setLoadError('No trading hours data found');
        }
      } catch (error) {
        console.error('Error loading trading hours:', error);
        setLoadError(error.message || 'Failed to load trading hours');
      } finally {
        setLoading(false);
      }
    };

    loadTradingHours();
  }, [formatTime]);

  // Calculate totals
  const calculateTotals = useCallback((hoursData) => {
    let workingDays = 0;
    let weeklyHours = 0;

    Object.values(hoursData).forEach(day => {
      if (day && day.open === true && day.start && day.end) {
        try {
          const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
          
          if (!timeRegex.test(day.start) || !timeRegex.test(day.end)) {
            console.warn('Invalid time format detected:', day);
            return;
          }

          workingDays++;
          
          const startTime = new Date(`2000-01-01T${day.start}:00`);
          const endTime = new Date(`2000-01-01T${day.end}:00`);
          
          if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
            console.warn('Invalid date objects created from times:', day);
            return;
          }
          
          const hoursPerDay = (endTime - startTime) / (1000 * 60 * 60);
          
          if (hoursPerDay > 0) {
            weeklyHours += hoursPerDay;
          }
        } catch (dayError) {
          console.error('Error calculating hours for day:', day, dayError);
        }
      }
    });

    return { workingDays, weeklyHours };
  }, []);

  // Save trading hours to database with debouncing
  const saveTradingHours = useCallback(async (hoursData, workingDays, weeklyHours) => {
    if (!initialLoadRef.current || loading) {
      console.log('Skipping save - not ready:', !initialLoadRef.current, 'loading:', loading);
      return;
    }
    
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Debounce the save operation
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        console.log('Saving trading hours:', hoursData);
        
        // Convert component format to flat API format
        const apiData = {};
        Object.keys(hoursData).forEach(day => {
          const dayData = hoursData[day];
          apiData[`${day}_open`] = dayData.open;
          apiData[`${day}_start`] = formatTime(dayData.start);
          apiData[`${day}_end`] = formatTime(dayData.end);
        });
        
        apiData.total_working_days = workingDays;
        apiData.total_working_hours = weeklyHours;
        
        await tradingHoursService.saveTradingHours(apiData);
        console.log('Trading hours saved to database');
      } catch (error) {
        console.error('Error saving trading hours:', error);
      }
    }, 1000);
  }, [loading, formatTime]);

  // Update totals and notify parent when hours change
  useEffect(() => {
    if (loading || !initialLoadRef.current) {
      console.log('Skipping calculation - loading:', loading, 'initialLoad:', initialLoadRef.current);
      return;
    }

    console.log('Calculating totals for hours change...');
    const { workingDays, weeklyHours } = calculateTotals(hours);
    
    // Only update state if values actually changed
    setTotalDays(prev => {
      if (prev !== workingDays) {
        console.log('Updating total days:', prev, '->', workingDays);
        return workingDays;
      }
      return prev;
    });
    
    setTotalHours(prev => {
      if (Math.abs(prev - weeklyHours) > 0.01) {
        console.log('Updating total hours:', prev, '->', weeklyHours);
        return weeklyHours;
      }
      return prev;
    });

    // Notify parent component
    if (onChangeRef.current) {
      onChangeRef.current({
        tradingHours: hours,
        totalWorkingDays: workingDays,
        totalWorkingHours: weeklyHours
      });
    }

    // Save to database
    saveTradingHours(hours, workingDays, weeklyHours);
    
  }, [hours, loading, calculateTotals, saveTradingHours]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleDayToggle = useCallback((day) => {
    if (loading) return;
    
    setHours(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        open: !prev[day].open
      }
    }));
  }, [loading]);

  const handleTimeChange = useCallback((day, field, value) => {
    if (loading) return;
    
    // Don't format immediately - let the user type, but validate on blur/save
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (timeRegex.test(value)) {
      setHours(prev => ({
        ...prev,
        [day]: {
          ...prev[day],
          [field]: value
        }
      }));
    }
  }, [loading]);

  const quickSetHours = useCallback((startTime, endTime) => {
    if (loading) return;
    
    const formattedStart = formatTime(startTime);
    const formattedEnd = formatTime(endTime);

    setHours(prev => {
      const newHours = { ...prev };
      
      Object.keys(newHours).forEach(day => {
        if (newHours[day] && newHours[day].open) {
          newHours[day] = {
            ...newHours[day],
            start: formattedStart,
            end: formattedEnd
          };
        }
      });
      
      return newHours;
    });
  }, [loading, formatTime]);

  if (loading) {
    return (
      <div className="compact-trading-hours">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Loading trading hours...
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="compact-trading-hours">
        <div style={{ textAlign: 'center', padding: '20px', color: '#e74c3c' }}>
          Error: {loadError}
          <br />
          <small>Using default hours</small>
        </div>
      </div>
    );
  }

  const days = [
    { key: 'monday', label: 'Mon' },
    { key: 'tuesday', label: 'Tue' },
    { key: 'wednesday', label: 'Wed' },
    { key: 'thursday', label: 'Thu' },
    { key: 'friday', label: 'Fri' },
    { key: 'saturday', label: 'Sat' },
    { key: 'sunday', label: 'Sun' }
  ];

  return (
    <div className="compact-trading-hours">
      <div className="quick-set-buttons">
        <button 
          type="button"
          className="quick-set-btn"
          onClick={() => quickSetHours('09:00', '17:00')}
        >
          9-5
        </button>
        <button 
          type="button"
          className="quick-set-btn"
          onClick={() => quickSetHours('10:00', '18:00')}
        >
          10-6
        </button>
        <button 
          type="button"
          className="quick-set-btn"
          onClick={() => quickSetHours('09:00', '20:00')}
        >
          9-8
        </button>
      </div>

      <div className="days-grid">
        {days.map(({ key, label }) => {
          const dayData = hours[key];
          if (!dayData) return null;

          return (
            <div key={key} className={`day-row ${dayData.open ? 'open' : 'closed'}`}>
              <div className="day-header">
                <label className="day-checkbox">
                  <input
                    type="checkbox"
                    checked={dayData.open || false}
                    onChange={() => handleDayToggle(key)}
                  />
                  <span className="day-label">{label}</span>
                </label>
              </div>
              
              {dayData.open && (
                <div className="time-inputs">
                  <input
                    type="time"
                    value={dayData.start || '09:00'}
                    onChange={(e) => handleTimeChange(key, 'start', e.target.value)}
                    className="time-input"
                    aria-label={`${label} opening time`}
                  />
                  <span className="time-separator">-</span>
                  <input
                    type="time"
                    value={dayData.end || '17:00'}
                    onChange={(e) => handleTimeChange(key, 'end', e.target.value)}
                    className="time-input"
                    aria-label={`${label} closing time`}
                  />
                </div>
              )}
              
              {!dayData.open && (
                <div className="closed-indicator">
                  <span>Closed</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="hours-summary">
        <div className="summary-item">
          <span className="summary-label">Working Days:</span>
          <span className="summary-value">{totalDays} days/week</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Total Hours:</span>
          <span className="summary-value">{Number(totalHours).toFixed(1)} hrs/week</span>
        </div>
      </div>

      {totalHours > 0 && (
        <div className="hours-note">
          <small>These hours are used to calculate overhead per minute and owner profit per minute.</small>
        </div>
      )}
    </div>
  );
};

export default CompactTradingHours;
